<script>
import { mapGetters } from 'vuex';
import { googleService } from '../services/google-service';
import WootSubmitButton from 'dashboard/components/buttons/FormSubmitButton.vue';
import SelectTimes from './SelectTimes.vue';
import BlockedPeriods from './BlockedPeriods.vue';
import Services from './Services.vue';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    WootSubmitButton,
    SelectTimes,
    BlockedPeriods,
    Services,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    bookableTimes: {
      type: Object,
      default: () => {},
    },
    blockedPeriods: {
      type: Array,
      default: () => [],
    },
    services: {
      type: Array,
      default: () => [],
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      show: true,
      updateAutoOffline: true,
      currentUserAutoOffline: true,
      showHelpMessage: 0,
      helpMessages: [
        '',
        'O nome da agenda deve ser alterado na conta Google',
        'Os horários livres serão mostrados no chat exatamente como você os colocar abaixo',
        'Os nomes dos serviços são mostrados no chat e ocuparão esse mesmo espaço de tempo dentro da agenda',
        'Os intervalos bloqueados te ajudam nas pausas, horário de almoço etc. São aplicados em todos os dias da semana.',
      ],
      helpMessage: '',
      weekdays: [
        { en: 'Monday', pt_br: 'Segunda-feira' },
        { en: 'Tuesday', pt_br: 'Terça-feira' },
        { en: 'Wednesday', pt_br: 'Quarta-feira' },
        { en: 'Thursday', pt_br: 'Quinta-feira' },
        { en: 'Friday', pt_br: 'Sexta-feira' },
        { en: 'Saturday', pt_br: 'Sábado' },
        { en: 'Sunday', pt_br: 'Domingo' },
      ],
      currentBookableTimes: {},
      currentBlockedPeriods: [...this.blockedPeriods],
      currentServices: [],
      showBlockedPeriods: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'agents/getUIFlags',
    }),
  },
  created() {
    this.setCurrentBookableTimes();
    this.setCurrentServices();
  },
  methods: {
    showAlert(message) {
      emitter.emit('newToastMessage', message);
    },
    // change_name message -> param = 1
    // cookable_times message -> param = 2
    showMsg(number) {
      this.showHelpMessage = number;
      this.helpMessage = this.helpMessages[number] || '';
    },
    removeMsg(number) {
      if (number === this.showHelpMessage) this.showHelpMessage = 0;
    },

    setCurrentBookableTimes() {
      // If bookableTimes is an empty object
      if (!this.bookableTimes || !Object.keys(this.bookableTimes).length)
        this.makeDefaultAllWeekdays();
      else {
        // Deep copy it
        this.currentBookableTimes = JSON.parse(
          JSON.stringify(this.bookableTimes)
        );
      }
    },
    setCurrentServices() {
      if (this.services)
        // Deep copy
        this.currentServices = JSON.parse(JSON.stringify(this.services));
    },

    makeDefaultAllWeekdays() {
      // List times '08:00' till '16:00', step of 1 hour -> ["08:00","09:00" ... "16:00"]
      const defaultTimes = Array.from({ length: 9 }, (_, i) => {
        const hour = 8 + i;
        return `${hour.toString().padStart(2, '0')}:00`;
      });
      const weekDays = this.weekdays.map(e => e.en);
      const defaultBookableTimes = Object.fromEntries(
        weekDays.map(weekDay => [
          weekDay,
          {
            active: false,
            horarios: defaultTimes,
          },
        ])
      );
      this.currentBookableTimes = defaultBookableTimes;
    },
    updateShowBlockedPeriods() {
      this.showBlockedPeriods = !this.showBlockedPeriods;
    },
    updateWeekdayActive(weekday) {
      this.currentBookableTimes[weekday].active =
        !this.currentBookableTimes[weekday].active;
    },
    // For child components update this variable
    updateCurrentBookableTimes(updatedValue) {
      this.currentBookableTimes = updatedValue;
    },
    updateCurrentBlockedPeriods(updatedValue) {
      this.currentBlockedPeriods = updatedValue;
    },
    updateCurrentServices(updatedValue) {
      this.currentServices = updatedValue;
    },

    /* eslint-disable */
    updateChangedFields() {
      const equalObjects = (a, b) => JSON.stringify(a) === JSON.stringify(b);

      const changedBT = !equalObjects(
        this.currentBookableTimes,
        this.bookableTimes
      );
      if (changedBT) this.updateBookableTimes();

      const changedBP = !equalObjects(
        this.currentBlockedPeriods,
        this.blockedPeriods
      );
      if (changedBP) this.updateBlockedPeriods();

      const changedServices = !equalObjects(
        this.currentServices,
        this.services
      );
      if (changedServices) this.updateServices();

      if (!changedBT && !changedBP && !changedServices)
        return useAlert(`Nenhum alteração foi feita`);
      useAlert(`✅  -  Sua agenda foi atualizada com sucesso!`);
      this.onClose();
      location.reload(true); // Refresh page without cache
    },
    /* eslint-enable */

    // Requisitons
    async updateBookableTimes() {
      try {
        const response = await googleService.updateBookableTimes({
          schedule_id: this.id,
          bookable_times: this.currentBookableTimes,
        });
        if (response.status === 200) return true;
        return false;
      } catch (error) {
        useAlert(
          '❌  - Não foi possível atualizar os horários disponíveis dessa agenda. Por favor, tente novamente mais tarde'
        );
        return false;
      }
    },
    async updateBlockedPeriods() {
      try {
        const response = await googleService.updateBlockedPeriods({
          schedule_id: this.id,
          blocked_periods: this.currentBlockedPeriods,
        });
        if (response.status === 200) return true;
        return false;
      } catch (error) {
        useAlert(
          '❌  - Não foi possível atualizar os horários bloqueados dessa agenda. Por favor, tente novamente mais tarde'
        );
        return false;
      }
    },
    async updateServices() {
      try {
        const response = await googleService.updateServices({
          schedule_id: this.id,
          services: this.currentServices,
        });
        if (response.status === 200) return true;
        return false;
      } catch (error) {
        useAlert(
          '❌  - Não foi possível atualizar os serviços dessa agenda. Por favor, tente novamente mais tarde'
        );
        return false;
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="show" :on-close="onClose" size="modal-big">
    <div class="column content-box">
      <woot-modal-header
        header-title=""
        header-content="Editando agenda atual"
      />
      <form class="column medium-16" @submit.prevent="updateChangedFields">
        <div class="col-md-12 col-lg-12">
          <h5 class="text-slate-800 dark:text-slate-100 mx-auto">
            Agenda {{ name }}
          </h5>
        </div>

        <!-- Start calendar name -->
        <div
          class="col-md-12 col-lg-12 text-left"
          @mouseover="showMsg(1)"
          @mouseout="removeMsg(1)"
        >
          <label class="category-title">
            Mudar nome da agenda?
            <fluent-icon icon="info" size="14" />
          </label>
          <p v-show="showHelpMessage == 1" class="hover-message">
            {{ helpMessage }}
          </p>
        </div>
        <!-- End calendar name -->

        <hr class="operator__line" />

        <!-- Start Bookable Times -->
        <div
          class="col-md-12 col-lg-12 text-left"
          @mouseover="showMsg(2)"
          @mouseout="removeMsg(2)"
        >
          <label class="category-title">
            Horários disponíveis
            <fluent-icon icon="info" size="14" />
          </label>
          <p v-show="showHelpMessage == 2" class="hover-message">
            {{ helpMessage }}
          </p>
        </div>
        <div class="bookable-times-container flex">
          <div
            v-for="weekday in weekdays"
            :key="weekday.en"
            class="each-item-bookable-time"
          >
            <label
              class="weekday-label flex"
              :class="{
                active_weekday: currentBookableTimes[weekday.en].active,
              }"
            >
              {{ weekday.pt_br }}
              <woot-switch
                :value="currentBookableTimes[weekday.en].active"
                @input="updateWeekdayActive(weekday.en)"
              />
            </label>
            <transition name="slide-up">
              <SelectTimes
                v-if="currentBookableTimes[weekday.en].active"
                v-model:bookable-times="currentBookableTimes"
                :current-weekday="weekday.en"
                @update-current-bookable-times="updateCurrentBookableTimes"
              />
            </transition>
          </div>
          <!-- End Bookable Times -->
          <!-- Start Blocked periods -->
          <div class="each-item-bookable-time blocked-periods">
            <label
              class="weekday-label b_i_comp flex"
              :class="{
                active_weekday: showBlockedPeriods,
              }"
              @click="updateShowBlockedPeriods"
            >
              <div
                class="blocked-interval flex text-xs"
                @mouseover="showMsg(4)"
                @mouseout="removeMsg(4)"
              >
                <span>Intervalos</span>
                <span>bloqueados</span>
              </div>
              <fluent-icon
                v-if="showBlockedPeriods"
                icon="apps-list-detail"
                size="24"
              />
              <fluent-icon v-else icon="dismiss-circle" size="24" />
              <p v-show="showHelpMessage == 4" class="hover-message b_i_comp">
                {{ helpMessage }}
              </p>
            </label>
            <transition name="slide-up">
              <BlockedPeriods
                v-if="showBlockedPeriods"
                v-model:blocked-periods="currentBlockedPeriods"
                @update-blocked-periods="updateCurrentBlockedPeriods"
              />
            </transition>
          </div>
          <!-- End Blocked periods -->
        </div>

        <hr class="operator__line" />

        <!-- Start Services -->
        <div
          class="col-md-12 col-lg-12 text-left"
          @mouseover="showMsg(3)"
          @mouseout="removeMsg(3)"
        >
          <label class="category-title">
            Serviços
            <fluent-icon icon="info" size="14" />
          </label>
          <p v-show="showHelpMessage == 3" class="hover-message">
            {{ helpMessage }}
          </p>
        </div>
        <Services
          v-model:services="currentServices"
          @update-services="updateCurrentServices"
        />
        <!-- End Services -->

        <hr class="operator__line" />

        <div class="medium-offset-12 modal-footer w-full">
          <div class="medium-6 columns">
            <WootSubmitButton
              :disabled="false"
              button-text="Salvar alterações"
            />
            <button class="button clear" @click.prevent="onClose">
              {{ $t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<style scoped lang="scss">
.operator__line {
  width: 70%;
  right: 50%;
  left: 50%;
  border-bottom: 1px solid var(--color-border);
}
.flex {
  display: flex;
}
.category-title {
  gap: 0.2rem;
  display: flex;
  align-items: center;
  margin-top: 2.2rem;
  font-size: 1.2rem;
  font-weight: bold;
}
.hover-message {
  position: absolute;
  color: var(--white);
  background-color: var(--w-500);
  padding: 5px;
  border: 1px solid var(----b-200);
  border-radius: 5px;
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.301);
  z-index: 100;
  padding: 2%;

  &.b_i_comp {
    background-color: var(--r-500);
    transform: translate(-50%, -50%);
  }
}
.select-time {
  width: 55px;
  font-size: 1.35rem;
  height: 35px;
}
.bookable-times-container {
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 5px;
  gap: calc(5px + 1vw);

  .each-item-bookable-time {
    width: 145px;
    margin: 0;

    .weekday-label {
      background-color: var(--w-100);
      height: 40px;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      gap: 5px;
      border-radius: 15px;
      color: black;

      &.active_weekday {
        border-radius: 0;
        border-top-left-radius: 15px;
        border-top-right-radius: 15px;
      }

      &.b_i_comp {
        background-color: var(--r-700);
        color: var(--white);
        gap: 7px;
        cursor: pointer;

        .blocked-interval {
          line-height: 0.8;
          flex-direction: column;
        }
      }
    }
  }
}
.slide-up-transition-enter-active,
.slide-up-transition-leave-active {
  transition: transform 0.5s;
}
.slide-up-transition-enter,
.slide-up-transition-leave-to {
  transform: translateY(-100%);
  opacity: 0;
  transition-delay: 0.1s;
}

@media (max-width: 2400px) {
  .bookable-times-container {
    max-width: 790px;
    margin: 0 auto;
  }
}
</style>
